import React, { useState, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material'
import StyledListButton from '../UIComponents/StyledListButton'
import { withTranslator } from '../HOCComponents/withTranslator'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { useLocation } from 'react-router-dom'
import { trackEvent } from '../analytics/analytics'
import { TRACK_EVENTS } from '../../constants'
import { closeButtonLook } from '../UIComponents/CloseModalButton'

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  border: 1px solid ${themeColors.lightGreySolid};
  border-radius: 4px;
  margin-bottom: 12px;
`

const PreviewPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  border: 1px dashed ${themeColors.lightGreySolid};
  border-radius: 4px;
  margin-bottom: 12px;
  color: ${themeColors.lightGreySolid};
`

const StyledDialogTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 4px;
  h2 {
    font-size: 2.5rem;
    margin: 0;
  }
`

const CloseModalButton = styled.button`
  ${closeButtonLook}
  border: none;
  font-size: 1.7rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  height: 2.5rem;
  width: 2.5rem;
  cursor: pointer;
  i.fas {
    width: auto;
    height: auto;
  }
`

function ShareScreenshotButton({ t, CustomButton }) {
  const [open, setOpen] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [screenshotUrl, setScreenshotUrl] = useState(null)
  const [sharing, setSharing] = useState(false)
  const [error, setError] = useState(null)
  const blobRef = useRef(null)
  const { pathname } = useLocation()

  const handleOpen = useCallback(async () => {
    trackEvent(TRACK_EVENTS.SHARE_SCREENSHOT_BUTTON_CLICKED, { location: pathname })
    setScreenshotUrl(null)
    setError(null)
    blobRef.current = null

    if (!navigator.mediaDevices?.getDisplayMedia) {
      setError(t('Not supported by browser'))
      setOpen(true)
      return
    }

    setCapturing(true)

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        preferCurrentTab: true
      })

      const video = document.createElement('video')
      video.srcObject = stream
      await video.play()

      // Wait a frame for the video to be ready
      await new Promise(resolve => requestAnimationFrame(resolve))

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d').drawImage(video, 0, 0)

      stream.getTracks().forEach(track => track.stop())

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
      if (blob) {
        blobRef.current = blob
        setScreenshotUrl(URL.createObjectURL(blob))
        trackEvent(TRACK_EVENTS.SHARE_SCREENSHOT_CAPTURED, { location: pathname })
      }
    } catch {
      // user denied or capture failed
    }

    setCapturing(false)
    setOpen(true)
  }, [t, pathname])

  const closeDialog = useCallback(() => {
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl)
    setOpen(false)
    setScreenshotUrl(null)
    setError(null)
    blobRef.current = null
  }, [screenshotUrl])

  const handleClose = useCallback(() => {
    trackEvent(TRACK_EVENTS.SHARE_SCREENSHOT_CLOSED, { location: pathname })
    closeDialog()
  }, [closeDialog, pathname])

  const handleCancel = useCallback(() => {
    trackEvent(TRACK_EVENTS.SHARE_SCREENSHOT_CANCELLED, { location: pathname })
    closeDialog()
  }, [closeDialog, pathname])

  const handleShare = useCallback(async () => {
    if (!blobRef.current) return
    trackEvent(TRACK_EVENTS.SHARE_SCREENSHOT_SHARED, { location: pathname })
    setSharing(true)

    const file = new File([blobRef.current], 'screenshot.png', { type: 'image/png' })

    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] })
      }
    } catch (err) {
      // user cancelled share sheet
    }

    setSharing(false)
    closeDialog()
  }, [closeDialog, pathname])

  const handleCopy = useCallback(async () => {
    if (!blobRef.current) return
    trackEvent(TRACK_EVENTS.SHARE_SCREENSHOT_COPIED, { location: pathname })
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blobRef.current })])
    } catch {
      setError(t('Not supported by browser'))
    }
  }, [t, pathname])

  const handleDownload = useCallback(() => {
    if (!blobRef.current) return
    trackEvent(TRACK_EVENTS.SHARE_SCREENSHOT_DOWNLOADED, { location: pathname })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blobRef.current)
    link.download = 'screenshot.png'
    link.click()
    URL.revokeObjectURL(link.href)
  }, [pathname])

  return (
    <>
      {CustomButton ? (
        <CustomButton t={t} onClick={handleOpen} testId="share-screenshot-button-custom" />
      ) : (
        <StyledListButton label={t('Share screenshot')} onClick={handleOpen} icon="fas fa-share-nodes" testId="share-screenshot-button" />
      )}

      <Dialog open={open} onClose={() => {}} maxWidth={false} disableScrollLock PaperProps={{ sx: { width: '80vw', maxHeight: '90vh' } }}>
        <StyledDialogTitle>
          <h2>{t('Share screenshot')}</h2>
          <CloseModalButton onClick={handleClose}>
            <i className="fas fa-times" />
          </CloseModalButton>
        </StyledDialogTitle>

        <DialogContent>
          {error ? (
            <PreviewPlaceholder>{error}</PreviewPlaceholder>
          ) : capturing ? (
            <PreviewPlaceholder>
              <CircularProgress size={40} />
            </PreviewPlaceholder>
          ) : screenshotUrl ? (
            <PreviewImage src={screenshotUrl} alt="screenshot" />
          ) : (
            <PreviewPlaceholder>{t('No screenshot available')}</PreviewPlaceholder>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: '8px 24px 16px' }}>
          <Button onClick={handleCancel} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleCopy} disabled={capturing || !screenshotUrl} sx={{ textTransform: 'none' }}>
            {t('Copy')}
          </Button>
          <Button onClick={handleDownload} disabled={capturing || !screenshotUrl} sx={{ textTransform: 'none' }}>
            {t('Download')}
          </Button>
          <Button onClick={handleShare} variant="contained" disabled={capturing || sharing || !screenshotUrl} sx={{ textTransform: 'none' }}>
            {sharing ? <CircularProgress size={20} /> : t('share')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default withTranslator(ShareScreenshotButton)
